import { useMemo, useState } from 'react'
import { linkCriteriaToInvoice } from '../../services/licensing'
import type { LicenseCriteria, Organisation, XeroInvoiceState } from '../../types'

interface CreateLicenseGatesInput {
  organisations: Organisation[]
  xeroInvoices: XeroInvoiceState[]
  licenseCriteria: LicenseCriteria[]
  onCreate: (orgId: string, xeroId: string, criteriaId: string) => Promise<void>
  onRefresh: () => Promise<void>
  onClose: () => void
}

export interface CreateLicenseGatesState {
  selectedOrgId: string
  selectedXeroId: string
  setSelectedXeroId: (id: string) => void
  selectedCriteriaToLink: string
  setSelectedCriteriaToLink: (id: string) => void
  creating: boolean
  linking: boolean
  error: string | null
  selectedOrg: Organisation | undefined
  selectedXero: XeroInvoiceState | undefined
  availableInvoices: XeroInvoiceState[]
  usedInvoices: XeroInvoiceState[]
  linkableCriteria: LicenseCriteria[]
  linkedCriteria: LicenseCriteria | null
  hasPaymentGate: boolean
  hasLicenseGate: boolean
  canCreate: boolean
  paymentGateMessage: string
  licenseGateMessage: string
  handleOrgChange: (orgId: string) => void
  handleLinkCriteria: () => Promise<void>
  handleCreate: () => Promise<void>
}

interface DerivedData {
  availableInvoices: XeroInvoiceState[]
  usedInvoices: XeroInvoiceState[]
  linkedCriteria: LicenseCriteria | null
  readyForActivation: XeroInvoiceState[]
  linkableCriteria: LicenseCriteria[]
}

interface DerivedDataInput {
  selectedOrgId: string
  selectedXeroId: string
  xeroInvoices: XeroInvoiceState[]
  licenseCriteria: LicenseCriteria[]
}

function useDerivedData(deps: DerivedDataInput): DerivedData {
  const { selectedOrgId, selectedXeroId, xeroInvoices, licenseCriteria } = deps
  const allOrgPaidInvoices = useMemo(() => {
    if (!selectedOrgId) return []
    return xeroInvoices.filter(
      (entry) =>
        entry.organisationId === selectedOrgId &&
        entry.paymentGatePasses &&
        entry.invoiceStatus === 'PAID'
    )
  }, [selectedOrgId, xeroInvoices])

  const availableInvoices = useMemo(
    () => allOrgPaidInvoices.filter((entry) => !entry.entitlementId),
    [allOrgPaidInvoices]
  )
  const usedInvoices = useMemo(
    () => allOrgPaidInvoices.filter((entry) => entry.entitlementId !== null),
    [allOrgPaidInvoices]
  )
  const linkedCriteria = useMemo(() => {
    if (!selectedXeroId) return null
    return (
      licenseCriteria.find(
        (entry) => entry.xeroInvoiceStateId === selectedXeroId && entry.status === 'verified'
      ) ?? null
    )
  }, [selectedXeroId, licenseCriteria])
  const readyForActivation = useMemo(
    () =>
      availableInvoices.filter((invoice) =>
        licenseCriteria.some(
          (criterion) =>
            criterion.xeroInvoiceStateId === invoice.id && criterion.status === 'verified'
        )
      ),
    [availableInvoices, licenseCriteria]
  )
  const linkableCriteria = useMemo(() => {
    if (!selectedOrgId) return []
    return licenseCriteria.filter(
      (criterion) =>
        criterion.organisationId === selectedOrgId &&
        criterion.status === 'verified' &&
        criterion.isOrphaned
    )
  }, [selectedOrgId, licenseCriteria])
  return { availableInvoices, usedInvoices, linkedCriteria, readyForActivation, linkableCriteria }
}

interface HandlersInput {
  selectedOrgId: string
  selectedXeroId: string
  selectedCriteriaToLink: string
  linkedCriteria: LicenseCriteria | null
  canCreate: boolean
  setSelectedOrgId: (id: string) => void
  setSelectedXeroId: (id: string) => void
  setSelectedCriteriaToLink: (id: string) => void
  setError: (error: string | null) => void
  setLinking: (linking: boolean) => void
  setCreating: (creating: boolean) => void
  onCreate: (orgId: string, xeroId: string, criteriaId: string) => Promise<void>
  onRefresh: () => Promise<void>
  onClose: () => void
}

interface Handlers {
  handleOrgChange: (orgId: string) => void
  handleLinkCriteria: () => Promise<void>
  handleCreate: () => Promise<void>
}

function buildHandlers(input: HandlersInput): Handlers {
  const handleOrgChange = (orgId: string): void => {
    input.setSelectedOrgId(orgId)
    input.setSelectedXeroId('')
    input.setError(null)
  }
  const handleLinkCriteria = async (): Promise<void> => {
    if (!input.selectedCriteriaToLink || !input.selectedXeroId) return
    input.setLinking(true)
    input.setError(null)
    try {
      await linkCriteriaToInvoice(input.selectedCriteriaToLink, input.selectedXeroId)
      await input.onRefresh()
      input.setSelectedCriteriaToLink('')
    } catch (linkError) {
      input.setError(linkError instanceof Error ? linkError.message : 'Failed to link criteria')
    } finally {
      input.setLinking(false)
    }
  }
  const handleCreate = async (): Promise<void> => {
    if (!input.canCreate || !input.linkedCriteria) return
    input.setCreating(true)
    input.setError(null)
    try {
      await input.onCreate(input.selectedOrgId, input.selectedXeroId, input.linkedCriteria.id)
      input.onClose()
    } catch (createError) {
      input.setError(
        createError instanceof Error ? createError.message : 'Failed to create license'
      )
    } finally {
      input.setCreating(false)
    }
  }
  return { handleOrgChange, handleLinkCriteria, handleCreate }
}

interface GateMessagesInput {
  hasPaymentGate: boolean
  hasLicenseGate: boolean
  derived: DerivedData
  xeroInvoices: XeroInvoiceState[]
  licenseCriteria: LicenseCriteria[]
  selectedOrgId: string
}

function useGateMessages(input: GateMessagesInput): {
  paymentGateMessage: string
  licenseGateMessage: string
} {
  const { hasPaymentGate, hasLicenseGate, derived, xeroInvoices, licenseCriteria, selectedOrgId } =
    input
  const paymentGateMessage = useMemo(
    () =>
      buildPaymentGateMessage({
        hasPaymentGate,
        availableCount: derived.availableInvoices.length,
        usedCount: derived.usedInvoices.length,
        xeroInvoices,
        selectedOrgId,
      }),
    [
      hasPaymentGate,
      derived.availableInvoices.length,
      derived.usedInvoices.length,
      xeroInvoices,
      selectedOrgId,
    ]
  )
  const licenseGateMessage = useMemo(
    () =>
      buildLicenseGateMessage({
        hasLicenseGate,
        readyCount: derived.readyForActivation.length,
        licenseCriteria,
        selectedOrgId,
      }),
    [hasLicenseGate, derived.readyForActivation.length, licenseCriteria, selectedOrgId]
  )
  return { paymentGateMessage, licenseGateMessage }
}

export function useCreateLicenseGatesState(
  input: CreateLicenseGatesInput
): CreateLicenseGatesState {
  const { organisations, xeroInvoices, licenseCriteria, onCreate, onRefresh, onClose } = input
  const [selectedOrgId, setSelectedOrgId] = useState('')
  const [selectedXeroId, setSelectedXeroId] = useState('')
  const [creating, setCreating] = useState(false)
  const [linking, setLinking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedCriteriaToLink, setSelectedCriteriaToLink] = useState('')

  const derived = useDerivedData({
    selectedOrgId,
    selectedXeroId,
    xeroInvoices,
    licenseCriteria,
  })
  const selectedOrg = organisations.find((org) => org.id === selectedOrgId)
  const selectedXero = xeroInvoices.find((entry) => entry.id === selectedXeroId)
  const hasPaymentGate = derived.availableInvoices.length > 0
  const hasLicenseGate = derived.readyForActivation.length > 0
  const canCreate = Boolean(selectedOrgId && selectedXeroId && derived.linkedCriteria)

  const { paymentGateMessage, licenseGateMessage } = useGateMessages({
    hasPaymentGate,
    hasLicenseGate,
    derived,
    xeroInvoices,
    licenseCriteria,
    selectedOrgId,
  })
  const handlers = buildHandlers({
    selectedOrgId,
    selectedXeroId,
    selectedCriteriaToLink,
    linkedCriteria: derived.linkedCriteria,
    canCreate,
    setSelectedOrgId,
    setSelectedXeroId,
    setSelectedCriteriaToLink,
    setError,
    setLinking,
    setCreating,
    onCreate,
    onRefresh,
    onClose,
  })

  return {
    selectedOrgId,
    selectedXeroId,
    setSelectedXeroId,
    selectedCriteriaToLink,
    setSelectedCriteriaToLink,
    creating,
    linking,
    error,
    selectedOrg,
    selectedXero,
    availableInvoices: derived.availableInvoices,
    usedInvoices: derived.usedInvoices,
    linkableCriteria: derived.linkableCriteria,
    linkedCriteria: derived.linkedCriteria,
    hasPaymentGate,
    hasLicenseGate,
    canCreate,
    paymentGateMessage,
    licenseGateMessage,
    ...handlers,
  }
}

interface PaymentGateMessageInput {
  hasPaymentGate: boolean
  availableCount: number
  usedCount: number
  xeroInvoices: XeroInvoiceState[]
  selectedOrgId: string
}

function buildPaymentGateMessage(input: PaymentGateMessageInput): string {
  const { hasPaymentGate, availableCount, usedCount, xeroInvoices, selectedOrgId } = input
  if (hasPaymentGate) {
    if (usedCount > 0) {
      return `${String(availableCount)} available (${String(usedCount)} already used)`
    }
    return `${String(availableCount)} paid invoice(s) available`
  }
  const linkedInvoices = xeroInvoices.filter((entry) => entry.organisationId === selectedOrgId)
  if (linkedInvoices.length === 0) return 'No invoices linked to this organisation'
  const linkedUsedCount = linkedInvoices.filter((entry) => entry.entitlementId !== null).length
  if (linkedUsedCount > 0) {
    return `All ${String(linkedUsedCount)} invoice(s) already used for licenses`
  }
  const unpaidCount = linkedInvoices.filter((entry) => !entry.paymentGatePasses).length
  if (unpaidCount > 0) return `${String(unpaidCount)} invoice(s) not yet fully paid`
  return 'No available paid invoices for this organisation'
}

interface LicenseGateMessageInput {
  hasLicenseGate: boolean
  readyCount: number
  licenseCriteria: LicenseCriteria[]
  selectedOrgId: string
}

function buildLicenseGateMessage(input: LicenseGateMessageInput): string {
  const { hasLicenseGate, readyCount, licenseCriteria, selectedOrgId } = input
  if (hasLicenseGate) return `${String(readyCount)} ready for activation`
  const allOrgCriteria = licenseCriteria.filter((entry) => entry.organisationId === selectedOrgId)
  if (allOrgCriteria.length === 0) return 'No license criteria created for this organisation'
  const orphanedCount = allOrgCriteria.filter((entry) => entry.isOrphaned).length
  const pendingCount = allOrgCriteria.filter((entry) => entry.status === 'pending').length
  if (orphanedCount > 0) return `${String(orphanedCount)} criteria missing invoice link`
  if (pendingCount > 0) return `${String(pendingCount)} criteria pending verification`
  return 'No verified criteria linked to available invoices'
}
